import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { scrollToElement } from 'lib/helpers';

import './PLPQuickLinks.scss';

function PLPQuickLinks({
    id,
    quickLinks,
}) {
    const location = useLocation();
    const navigate = useNavigate();
    const observerRef = useRef(null);
    const sectionVisibilityMap = useRef({}); // Keep track of visibility percentages

    // Function to handle scrolling to the clicked link
    const handleQuickLinkClick = (linkId) => {
        // Use scrollToElement for smooth scrolling
        scrollToElement(`#${linkId}`, { offset: -window.innerHeight / 6, behavior: 'smooth' });

        // Preserve query parameters while updating the hash
        const currentParams = new URLSearchParams(location.search);
        navigate({
            pathname: location.pathname,
            search: currentParams.toString(),
            hash: `#${linkId}`,
        });
    };

    const handleScroll = (entries) => {
        entries.forEach((entry) => {
            const id = entry.target.id;
            const visibility = entry.intersectionRatio; // How much of the element is visible
            sectionVisibilityMap.current[id] = visibility;

            // Find the element with the maximum visibility
            const visibleId = Object.entries(sectionVisibilityMap.current).reduce(
                (maxId, [currentId, currentVisibility]) =>
                    currentVisibility > (sectionVisibilityMap.current[maxId] || 0) ? currentId : maxId,
                null
            );

            if (visibleId && location.hash !== `#${visibleId}`) {
                const currentParams = new URLSearchParams(location.search);
                navigate({
                    pathname: location.pathname,
                    search: currentParams.toString(),
                    hash: `#${visibleId}`,
                }, { replace: true });
            }
        });
    };

    useEffect(() => {
        // Set up IntersectionObserver
        observerRef.current = new IntersectionObserver(handleScroll, {
            root: null, // Observe relative to the viewport
            rootMargin: '0px', // No margin
            threshold: Array.from({ length: 101 }, (_, i) => i / 100), // Track visibility changes in 1% increments
        });

        // Attach observer to each quick link's section
        quickLinks.forEach((link) => {
            const element = document.getElementById(link.id);
            if (element) {
                observerRef.current.observe(element);
            }
        });

        return () => {
            // Cleanup observer on unmount
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [quickLinks, location.hash]);

    return Array.isArray(quickLinks) ? (
        <div className="quick-links-wrapper" id={id}>
            <div className="quick-links-container">
                {quickLinks.map((link) => (
                    <span
                        key={link.id}
                        onClick={() => handleQuickLinkClick(link.id)}
                        className={classNames({ active: `#${link.id}` === location.hash })}
                    >
                        {link.label}
                    </span>
                ))}
            </div>
        </div>
    ) : null;
}

PLPQuickLinks.propTypes = {
    className: PropTypes.string,
    id: PropTypes.string,
    quickLinks: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
};

export default PLPQuickLinks;
